import React, { useEffect, useState } from "react";
import { Building2, Save } from "lucide-react";
import { toast } from "sonner";
import * as api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { Button } from "../ui/button";
import { UserSearchSelect } from "../ui/UserSearchSelect";

// Fiche item : attribution partagée (département) — responsable et utilisateur prévu modifiables
export function SharedAssignmentCard({ item, people, departments, onChanged }) {
    const { hasPermission } = useApp();
    const canManage = hasPermission("inventory:manage");
    const [departmentId, setDepartmentId] = useState(item.assigned_department_id || "");
    const [responsible, setResponsible] = useState(item.responsible_user_id || null);
    const [intended, setIntended] = useState(item.intended_user_id || null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDepartmentId(item.assigned_department_id || "");
        setResponsible(item.responsible_user_id || null);
        setIntended(item.intended_user_id || null);
    }, [item.assigned_department_id, item.responsible_user_id, item.intended_user_id]);

    const dirty = departmentId !== (item.assigned_department_id || "") || (responsible || null) !== (item.responsible_user_id || null) || (intended || null) !== (item.intended_user_id || null);
    const sortedDepts = [...(departments || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    const save = async () => {
        setSaving(true);
        try {
            await api.updateSharedAssignment(item.id, { department_id: departmentId, responsible_user_id: responsible || null, intended_user_id: intended || null });
            toast.success("Attribution partagée mise à jour");
            onChanged?.();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    return (
        <div className="card border-teal-200" data-testid="shared-assignment-card">
            <div className="card-header flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2"><Building2 className="w-4 h-4 text-teal-600" />Matériel partagé — attribué à un département</h3>
                {canManage && dirty && <Button size="sm" onClick={save} disabled={saving || !departmentId} data-testid="shared-assignment-save"><Save className="w-3.5 h-3.5 mr-1" />{saving ? "…" : "Enregistrer"}</Button>}
            </div>
            <div className="card-body grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className="form-label text-xs">Département</label>
                    <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} disabled={!canManage} className="w-full px-3 py-2 text-sm border rounded-md bg-white" data-testid="shared-department-select">
                        {sortedDepts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="form-label text-xs">Responsable <span className="text-gray-400 font-normal">(optionnel)</span></label>
                    {canManage ? <UserSearchSelect users={people} departments={departments} value={responsible} onChange={setResponsible} placeholder="Aucun" data-testid="shared-responsible-select" />
                        : <p className="text-sm text-gray-700">{people.find((p) => p.id === responsible) ? `${people.find((p) => p.id === responsible).first_name} ${people.find((p) => p.id === responsible).last_name}` : "—"}</p>}
                </div>
                <div>
                    <label className="form-label text-xs">Utilisateur prévu <span className="text-gray-400 font-normal">(optionnel)</span></label>
                    {canManage ? <UserSearchSelect users={people} departments={departments} value={intended} onChange={setIntended} placeholder="Aucun" data-testid="shared-intended-select" />
                        : <p className="text-sm text-gray-700">{people.find((p) => p.id === intended) ? `${people.find((p) => p.id === intended).first_name} ${people.find((p) => p.id === intended).last_name}` : "—"}</p>}
                </div>
            </div>
        </div>
    );
}
